import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Snackbar, Paper, Typography, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiHandler from '../handlers/ApiHandler';

// Social-proof ("FOMO") toasts. Driven ONLY by real first-party data from our
// own analytics API. Never fabricates activity: if the API is disabled or
// returns nothing, this renders nothing. Must never throw or block rendering.

const POLL_MS = 25000; // poll politely
const ROTATE_MS = 8000; // advance to next message
const VISIBLE_MS = 6000; // how long each toast stays open

// Common ISO alpha-2 -> friendly display name. Falls back to the raw code.
const COUNTRY_NAMES = {
  US: 'the US',
  CA: 'Canada',
  GB: 'the UK',
  UK: 'the UK',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  NL: 'the Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  IE: 'Ireland',
  ES: 'Spain',
  IT: 'Italy',
  CH: 'Switzerland',
  AT: 'Austria',
  BE: 'Belgium',
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  IN: 'India',
  BR: 'Brazil',
  MX: 'Mexico',
  NZ: 'New Zealand',
  SG: 'Singapore',
  PL: 'Poland',
  PT: 'Portugal',
  ZA: 'South Africa',
};

const countryName = (code) => {
  if (!code) return '';
  return COUNTRY_NAMES[code] || code;
};

const ACTION_TEXT = {
  signup: 'signed up',
  purchase: 'backed us',
  add_to_cart: 'added an item to their cart',
};

const buildMessages = (config, data, pathname) => {
  const messages = [];
  if (!config || !data) return messages;

  // Active visitor count. Use the per-path count on product pages, else site-wide.
  if (config.showActiveVisitors) {
    const onProductPath =
      typeof pathname === 'string' && pathname.indexOf('/product') === 0;
    const activeOnPath =
      data.activeOnPath === null || data.activeOnPath === undefined
        ? null
        : data.activeOnPath;
    const n = onProductPath && activeOnPath !== null
      ? activeOnPath
      : data.activeVisitors;
    const min = config.minActiveToShow || 0;
    if (typeof n === 'number' && n >= min && n > 0) {
      const noun = n === 1 ? 'person is' : 'people are';
      messages.push(`${n} ${noun} viewing this right now`);
    }
  }

  // Recent real events (last hour, most-recent first).
  if (config.showRecentActivity && Array.isArray(data.recent)) {
    data.recent.forEach((item) => {
      if (!item) return;
      const action = ACTION_TEXT[item.kind];
      if (!action) return;
      const place = countryName(item.country);
      const where = place ? ` in ${place}` : '';
      messages.push(`Someone${where} just ${action}`);
    });
  }

  return messages;
};

export default function FomoToasts() {
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // 1) Fetch config once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiHandler.get('/analytics/fomo-config');
        const cfg = res && res.config ? res.config : null;
        if (!cancelled) setConfig(cfg);
      } catch (e) {
        /* tolerate silently — never break the site */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2) Poll live data while enabled.
  const enabled = !!(config && config.enabled);

  const fetchLive = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const path = pathRef.current || '/';
      const res = await apiHandler.get(
        `/analytics/live?path=${encodeURIComponent(path)}`
      );
      const next = buildMessages(config, res, path);
      setMessages(next);
    } catch (e) {
      /* tolerate silently */
    }
  }, [config]);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchLive();
    const id = setInterval(fetchLive, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, fetchLive]);

  // 3) Rotate through the messages.
  useEffect(() => {
    if (!enabled || messages.length === 0) {
      setOpen(false);
      return undefined;
    }
    setIndex((i) => (i < messages.length ? i : 0));
    setOpen(true);
    const rotate = setInterval(() => {
      setIndex((i) => (i + 1) % messages.length);
      setOpen(true);
    }, ROTATE_MS);
    return () => clearInterval(rotate);
  }, [enabled, messages]);

  // 4) Auto-hide each toast a little before it rotates.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => setOpen(false), VISIBLE_MS);
    return () => clearTimeout(t);
  }, [open, index]);

  if (!enabled || messages.length === 0) return null;

  const text = messages[index % messages.length];
  if (!text) return null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      sx={{ maxWidth: 340 }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'success.main',
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{ color: 'text.primary', lineHeight: 1.3 }}
        >
          {text}
        </Typography>
        <IconButton
          size="small"
          aria-label="dismiss"
          onClick={() => setOpen(false)}
          sx={{ ml: 0.5, color: 'text.secondary' }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
}
